/**
 * Pricing utility functions
 */

/**
 * Format numbers with commas for better readability
 * 
 * @param {number} num - Number to format
 * @returns {string} Formatted number with commas
 */
export const formatNumber = (num) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

/**
 * Format price with dollar sign and decimals
 * 
 * @param {number} price - Price to format
 * @returns {string} Formatted price with $ and 2 decimal places
 */
export const formatPrice = (price) => {
  return `$${price.toFixed(2)}`;
};

/**
 * Calculate monthly price based on custom configuration.
 * This function anchors the minimum configuration
 * (1 agent, 50 queries, 10 documents) to $15/month.
 * It adds incremental costs for usage above the minimum:
 *   - $1.00 for each extra agent,
 *   - $0.02 for each extra query,
 *   - $0.01 for each extra document.
 *
 * At maximum limits (100 agents, 10,000 queries, 50,000 documents),
 * the monthly price exceeds $800.
 *
 * @param {number} agentCount - Number of agents
 * @param {number} queryCount - Number of queries
 * @param {number} documentCount - Number of documents
 * @returns {number} Calculated monthly price
 */
function calculateCustomPrice(agentCount, queryCount, documentCount) {
  // Minimum configuration values.
  const MIN_AGENTS = 1;
  const MIN_QUERIES = 50;
  const MIN_DOCUMENTS = 10;
  
  // Base monthly price for the minimum configuration.
  const basePrice = 15;
  
  // Additional cost multipliers for usage above the minimum.
  const additionalAgentPrice = 1.00;    // per extra agent
  const additionalQueryPrice = 0.02;    // per extra query
  const additionalDocumentPrice = 0.01; // per extra document

  // Calculate extra usage.
  const extraAgents = Math.max(0, agentCount - MIN_AGENTS);
  const extraQueries = Math.max(0, queryCount - MIN_QUERIES);
  const extraDocuments = Math.max(0, documentCount - MIN_DOCUMENTS);

  // Compute the total monthly price.
  const totalPrice = basePrice +
                     (extraAgents * additionalAgentPrice) +
                     (extraQueries * additionalQueryPrice) +
                     (extraDocuments * additionalDocumentPrice);

  return Math.round(totalPrice * 100) / 100;
}

export { calculateCustomPrice };
/**
 * Validate Square payment environment
 * Checks if environment variables are set and Square SDK is properly loaded
 * 
 * @returns {Object} Validation result with status and error message
 */
export const validateSquareEnvironment = () => {
  const result = {
    isValid: true,
    message: 'Square environment is valid',
    sandboxMode: process.env.NODE_ENV !== 'production'
  };
  
  // Check for required environment variables
  const appId = process.env.REACT_APP_SQUARE_APP_ID;
  const locationId = process.env.REACT_APP_SQUARE_LOCATION_ID;
  
  if (!appId) {
    result.isValid = false;
    result.message = 'Square App ID is missing. Check your environment variables.';
    return result;
  }
  
  if (!locationId) {
    result.isValid = false;
    result.message = 'Square Location ID is missing. Check your environment variables.';
    return result;
  }
  
  // Check if we're in sandbox mode (development)
  if (result.sandboxMode) {
    // For sandbox, ensure we're using sandbox credentials
    if (!appId.startsWith('sandbox-')) {
      result.isValid = false;
      result.message = 'In development mode, but not using sandbox credentials. Check your Square App ID.';
    }
  }
  
  return result;
}; 